/* eslint-disable react/jsx-wrap-multilines */
import { memo } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import InputAdornment from '@material-ui/core/InputAdornment';
import UnderlineLink from '@comp/UnderlineLink/UnderlineLink';
import H6 from '@base/H6/H6';
import H2 from '@base/H2/H2';
import P1 from '@base/P1/P1';
import Button from '@base/Button/Button';
import TextField from '@base/TextField/TextField';
import { Spacings, Fonts } from '@styles/index';

const FlexDiv = styled.div`
  display: flex;
  margin-top: ${Spacings.SPACING_3B};
  margin-bottom: ${Spacings.SPACING_8B};
`;

const StyledP1 = styled(P1)`
  margin-right: ${Spacings.SPACING_1B};
`;

const BoldP1 = styled(P1)`
  padding: 0 ${Spacings.SPACING_2B} 0 ${Spacings.SPACING_3B};
  font-weight: ${Fonts.SEMIBOLD};
`;

const StyledH6 = styled(H6)`
  font-weight: ${Fonts.BOLD};
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${Spacings.SPACING_8B};
`;

const EditDiv = styled.div`
  position: relative;
`;

const StyledSelectedMenu = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-right: ${Spacings.SPACING_2B};
`;

const StyledImg = styled.img`
  width: ${Spacings.SPACING_6B};
`;

const StyledTextField = styled(TextField)`
  margin-top: ${Spacings.SPACING_4B};
`;

const StyledP2 = styled.p`
  font-weight: ${Fonts.REGULAR};
  font-size: ${Fonts.P1};
  line-height: ${Fonts.HEADER_LINE_HEIGHT};
  margin: ${Spacings.SPACING_0B};
  color: ${({ color }) => color};
  margin-top: ${Spacings.SPACING_3B};
`;

const EnterNumberStep = ({
  accountType,
  changeAccountType,
  country,
  code,
  email,
  name,
  mobileNumber,
  setMobileNumber,
  setEmail,
  setCode,
  setName,
  submitNumber,
  errorMsg
}) => {
  const onNumberChange = (e) => {
    const phone = e.target.value.replace(/ /g, '');
    if (phone.match(/^\d+$/) || phone === '') {
      if (country?.code === 'IN' && phone.length > 10) {
        // do nothing
      } else {
        setMobileNumber(phone);
      }
    }
  };
  const validMobileNumber = () => {
    if (
      mobileNumber.match(/^\d+$/) &&
      country?.code === 'IN' &&
      mobileNumber.length !== 10
    ) {
      return false;
    }
    if (mobileNumber === '') {
      return false;
    }
    return true;
  };

  const toggleAccountType = (e) => {
    e.preventDefault();
    changeAccountType();
  };

  const onButtonClick = () => {
    if (accountType === 'login') {
      if (validMobileNumber()) submitNumber();
    } else {
      let isValid = validMobileNumber();
      if (name.trim() === '' || email.trim() === '' || code.trim() === '')
        isValid = false;
      if (isValid) {
        submitNumber();
      }
    }
  };

  const onFieldChange = (type) => (e) => {
    if (type === 'name') {
      setName(e.target.value);
    }
    if (type === 'email') {
      setEmail(e.target.value);
    }
    if (type === 'code') {
      setCode(e.target.value);
    }
  };

  const isLoginAccountType = accountType === 'login';

  return (
    <>
      <H2>{isLoginAccountType ? 'Login to teach' : 'Sign up to teach'}</H2>
      <FlexDiv>
        <StyledP1>or</StyledP1>
        <UnderlineLink
          color="var(--color-i-blue)"
          onClick={toggleAccountType}
          href="#"
        >
          <StyledH6 color="var(--color-i-blue)">
            {isLoginAccountType
              ? 'create your account'
              : 'login to your account'}
          </StyledH6>
        </UnderlineLink>
      </FlexDiv>
      <EditDiv>
        <TextField
          fullWidth
          startAdornment={
            <InputAdornment>
              <StyledSelectedMenu>
                <StyledImg src={country.flagIconUrl} alt={country.name} />
                <BoldP1>{`+${country.phoneCode}`}</BoldP1>
              </StyledSelectedMenu>
            </InputAdornment>
          }
          placeholder="Enter your mobile number"
          onChange={onNumberChange}
          value={mobileNumber}
          inputProps={{
            type: 'tel',
            maxLength: 15,
            minLength: 3,
            required: true
          }}
          type="tel"
          autoFocus
          // error={errorMsg !== ''}
          // helperText={errorMsg}
          enterCallback={onButtonClick}
        />
      </EditDiv>
      {!isLoginAccountType ? (
        <>
          <StyledTextField
            fullWidth
            value={name}
            type="text"
            placeholder="Name"
            onChange={onFieldChange('name')}
          />
          <StyledTextField
            fullWidth
            value={email}
            type="text"
            placeholder="Email address"
            onChange={onFieldChange('email')}
          />
          <StyledTextField
            fullWidth
            value={code}
            type="text"
            placeholder="Institute code"
            onChange={onFieldChange('code')}
          />
        </>
      ) : null}
      {errorMsg && errorMsg !== '' ? (
        <StyledP2 color="var(--color-i-red)">{errorMsg}</StyledP2>
      ) : null}
      <ButtonWrapper>
        <Button
          variant={validMobileNumber() ? 'filled' : 'disabled'}
          label={isLoginAccountType ? 'Login' : 'Sign up'}
          onClick={onButtonClick}
          color="blue"
          form="enter_number"
        />
      </ButtonWrapper>
    </>
  );
};

export default memo(EnterNumberStep);

EnterNumberStep.propTypes = {
  accountType: PropTypes.string,
  mobileNumber: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  code: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  country: PropTypes.objectOf(PropTypes.any).isRequired,
  changeAccountType: PropTypes.func.isRequired,
  setMobileNumber: PropTypes.func.isRequired,
  setEmail: PropTypes.func.isRequired,
  setCode: PropTypes.func.isRequired,
  setName: PropTypes.func.isRequired,
  submitNumber: PropTypes.func.isRequired,
  errorMsg: PropTypes.string.isRequired
};

EnterNumberStep.defaultProps = {
  accountType: 'signup'
};
